import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function ProfileEditPage() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({
        full_name: '', email: '', bio: '', location: '', phone: '', avatar_url: '', cover_url: '', country: 'mx',
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (user) setForm(f => ({ ...f, ...user }));
    }, [user]);

    const uploadAvatar = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setForm(f => ({ ...f, avatar_url: `${(process.env.REACT_APP_BACKEND_URL || '')}${data.url}` }));
            toast.success('Foto actualizada');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error de subida'); }
        finally { setUploading(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/me/profile', {
                full_name: form.full_name || null,
                email: form.email || null,
                bio: form.bio || null,
                location: form.location || null,
                phone: form.phone || null,
                avatar_url: form.avatar_url || null,
                cover_url: form.cover_url || null,
                country: (form.country || 'mx').toLowerCase(),
            });
            toast.success('Perfil actualizado');
            await refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4 max-w-2xl">
            <h1 className="text-xl font-semibold tracking-tight">Editar perfil</h1>

            <div className="rounded-2xl border bg-card p-6 space-y-4">
                <div className="flex items-center gap-4">
                    <div className="h-20 w-20 rounded-full overflow-hidden bg-red-100 border">
                        {form.avatar_url ? <img src={form.avatar_url} alt="" className="h-full w-full object-cover" /> :
                            <div className="h-full w-full flex items-center justify-center text-2xl font-bold text-red-600">{user?.nickname?.[0]?.toUpperCase() || '?'}</div>}
                    </div>
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-secondary text-sm">
                        {uploading ? 'Subiendo…' : 'Cambiar foto'}
                        <input type="file" hidden accept="image/*" onChange={uploadAvatar} data-testid="profile-avatar-upload" />
                    </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nickname</Label><Input value={user?.nickname || ''} disabled /></div>
                    <div><Label>Email</Label><Input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" /></div>
                    <div className="col-span-2"><Label>Nombre completo</Label><Input value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-fullname" /></div>
                    <div><Label>Teléfono</Label><Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><Label>Ubicación</Label><Input value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="profile-location" /></div>
                    <div><Label>País (código ISO2)</Label><Input value={form.country || 'mx'} maxLength={2} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="mx" /></div>
                    <div className="col-span-2"><Label>Bio</Label><Textarea value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} placeholder="Cuéntanos sobre ti como vendedor..." data-testid="profile-bio" /></div>
                </div>
                <Button onClick={save} disabled={saving} data-testid="profile-save">{saving ? 'Guardando…' : 'Guardar cambios'}</Button>
            </div>
        </div>
    );
}
